
import { useRef, useContext } from 'react';
import {
  MDBBtn, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBCardTitle, MDBIcon, MDBSpinner,
  MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter,
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useIndustry } from '../../utils/industryContextHook';
import { UserContext } from '../../App';
import ErrorFallback from '../../components/toolbox/ErrorFallback';
import SelectTopics from '../../components-user/topics/SelectTopics';
import EmployeeSelector from '../../components-user/news/EmployeeSelector';

import { useNewsForm } from './hooks/useNewsForm';
import NewsTopicSection from './components/NewsTopicSection';
import NewsContentSection from './components/NewsContentSection';
import NewsMediaSection from './components/NewsMediaSection';
import NewsContactSection from './components/NewsContactSection';

const SECTIONS = [
  { id: 1, title: "Topic", icon: "tags" },
  { id: 2, title: "Content", icon: "file-alt" },
  { id: 3, title: "Media", icon: "image" },
  { id: 4, title: "Contact", icon: "user" },
];

const CreateNewsPage = () => {
  const { t } = useTranslation();
  const { currentIndustry } = useIndustry();
  const { user } = useContext(UserContext);
  const isAdminUser = user?.["cognito:groups"]?.includes("Admin");

  // URL-based IDs
  const companyId = window.location.pathname.includes("mycompany")
    ? window.location.pathname.split("/")[2]
    : window.location.pathname.split("/")[3];

  let newsId = window.location.pathname.includes("mycompany")
    ? window.location.pathname.split("/")[4]
    : window.location.pathname.split("/")[5];

  newsId = newsId === "create" ? null : newsId;

  // Form logic hook
  const form = useNewsForm({ companyId, newsId, currentIndustry });

  // Refs for scroll-to-invalid
  const sectionRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
  };

  // Handle submit with scroll-to-first-invalid
  const handleSubmit = () => {
    const firstInvalid = form.validateAllAndGetFirstInvalid();
    if (firstInvalid) {
      sectionRefs[firstInvalid].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    form.createOrUpdateNews();
  };

  // Count completed sections
  const completedCount = SECTIONS.filter(s => form.isSectionComplete(s.id)).length;

  if (form.loadError) return <ErrorFallback />;

  if (form.isLoading) {
    return (
      <div className="text-center py-5">
        <MDBSpinner grow color="primary" />
      </div>
    );
  }

  // Render section content by id
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 1:
        return (
          <NewsTopicSection
            topics={form.topics}
            topicGroups={form.topicGroups}
            selectedTopics={form.selectedTopics}
            unknownTopicNames={form.unknownTopicNames}
            toggleSelectTopicsModal={form.toggleSelectTopicsModal}
            handleSetSelectedTopics={form.handleSetSelectedTopics}
            getFieldError={form.getFieldError}
            markFieldTouched={form.markFieldTouched}
          />
        );
      case 2:
        return (
          <NewsContentSection
            newsData={form.newsData}
            setNewsName={form.setNewsName}
            editorState={form.editorState}
            onEditorStateChange={form.onEditorStateChange}
            getFieldError={form.getFieldError}
            markFieldTouched={form.markFieldTouched}
          />
        );
      case 3:
        return (
          <NewsMediaSection
            newsData={form.newsData}
            companyId={companyId}
            handleSetImage={form.handleSetImage}
            isUploadingImage={form.isUploadingImage}
            uploadingImageError={form.uploadingImageError}
            handleSetFile={form.handleSetFile}
            isUploadingFile={form.isUploadingFile}
            uploadingFileError={form.uploadingFileError}
            pending3DModelFile={form.pending3DModelFile}
            preview3DModelUrl={form.preview3DModelUrl}
            isAdminUser={isAdminUser}
            upload3DModelError={form.upload3DModelError}
            onFileSelected={form.handleSet3DModel}
            onFileRemoved={form.handleRemove3DModel}
            handleConditionChange={form.handleConditionChange}
            getFieldError={form.getFieldError}
            markFieldTouched={form.markFieldTouched}
          />
        );
      case 4:
        return (
          <NewsContactSection
            newsData={form.newsData}
            employee={form.employee}
            toggleSelectEmployeeModal={form.toggleSelectEmployeeModal}
            getFieldError={form.getFieldError}
            markFieldTouched={form.markFieldTouched}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <MDBRow className="h-auto d-flex justify-content-center" style={{ margin: "0 0 3.5rem 0" }}>
        <MDBCol size="12" xl="12" xxl="11">
          <h4 className="mb-4">
            {form.isEditMode ? t("Edit News") : t("Create News")}
          </h4>

          {/* Section cards */}
          <div style={form.isSaving ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
            {SECTIONS.map((section) => {
              const isComplete = form.isSectionComplete(section.id);
              return (
                <div key={section.id} ref={sectionRefs[section.id]} className="mb-4">
                  <MDBCard>
                    <MDBCardBody className="px-3 px-md-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: '28px', height: '28px', fontSize: '14px' }}>
                          {section.id}
                        </span>
                        <MDBIcon fas icon={section.icon} className="text-muted" />
                        <MDBCardTitle tag="h5" className="mb-0">
                          {t(section.title)}
                        </MDBCardTitle>
                        {isComplete && (
                          <MDBIcon fas icon="check-circle" className="text-success ms-auto" size="lg" />
                        )}
                        {form.submitAttempted && !isComplete && (
                          <MDBIcon fas icon="exclamation-circle" className="text-danger ms-auto" size="lg" />
                        )}
                      </div>
                      {renderSectionContent(section.id)}
                    </MDBCardBody>
                  </MDBCard>
                </div>
              );
            })}
          </div>
          <MDBCard>
            <MDBCardBody>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">
                  {completedCount} {t("of")} {SECTIONS.length} {t("sections complete")}
                </span>
                <MDBBtn color="primary" onClick={handleSubmit} disabled={form.isSaving}>
                  {form.isSaving ? <MDBSpinner size="sm" /> : (form.isEditMode ? t("Update") : t("Create"))}
                </MDBBtn>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Topics Modal */}
      <MDBModal open={form.selectTopicsModal} onClose={() => form.setSelectTopicsModal(false)} staticBackdrop>
        <MDBModalDialog centered size="xl">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Select Topics")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={form.toggleSelectTopicsModal} />
            </MDBModalHeader>
            <MDBModalBody>
              <SelectTopics
                topics={form.topics}
                topicGroups={form.topicGroups}
                isActive={form.selectTopicsModal}
                toggleActive={form.toggleSelectTopicsModal}
                selectedTopics={form.selectedTopics}
                setSelectedTopics={form.handleSetSelectedTopics}
                parentTopics={form.topics}
                parentTopicGroups={form.topicGroups}
                maxSelect={1}
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={form.toggleSelectTopicsModal}>{t("Close")}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* Employee Modal */}
      <MDBModal open={form.selectEmployeeModal} onClose={() => form.setSelectEmployeeModal(false)} staticBackdrop>
        <MDBModalDialog centered size="xl">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Select employee")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={form.toggleSelectEmployeeModal} />
            </MDBModalHeader>
            <MDBModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EmployeeSelector
                companyId={companyId}
                selectedEmployees={form.newsData.cMSNewsEmployeeId ? [form.newsData.cMSNewsEmployeeId] : []}
                setSelectedEmployees={form.handleSetEmployee}
                maxSelect={1}
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={form.toggleSelectEmployeeModal}>{t("Close")}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
};

export default CreateNewsPage;
